import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';

/**
 * AppLayout scrolls its content area, not the window. The header above it is
 * 70px, so the visible scroll area is the viewport minus that.
 */
export const APP_HEADER_HEIGHT = 70;

export function appScroller(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  return document.querySelector<HTMLElement>('[data-app-scroll]');
}

/**
 * A scroll box that keeps the scroll to itself. At its top or bottom the wheel
 * or a trackpad fling stops there and does not move the page behind it.
 */
export const containedScroll: CSSProperties = {
  overflowY: 'auto',
  overscrollBehavior: 'contain',
};

/**
 * Height a sticky panel may take inside the app scroller: the visible scroll
 * area minus its sticky top offset and a matching bottom gap. Never below
 * `floor`, so a very short window still shows a usable panel.
 */
export function stickyPanelMaxHeight(
  scrollportHeight: number,
  top = 24,
  bottom = 24,
  floor = 240
): number {
  if (!Number.isFinite(scrollportHeight) || scrollportHeight <= 0) return floor;
  return Math.max(floor, Math.round(scrollportHeight - top - bottom));
}

/**
 * Where to scroll so a list's top sits at the top of the scroller, or null when
 * the list top is already on screen. `anchorTop` is the anchor's top measured
 * from the scroller's top edge (negative once it has scrolled past).
 */
export function listTopScrollTarget(
  scrollTop: number,
  anchorTop: number,
  margin = 16
): number | null {
  if (anchorTop >= margin) return null;
  return Math.max(0, Math.round(scrollTop + anchorTop - margin));
}

/**
 * A background refresh refetches page 1 only. Once more pages are loaded that
 * would cut the list short and pull the page up, so it skips the list.
 */
export function shouldRefreshList(loadedCount: number, pageSize: number): boolean {
  return loadedCount <= pageSize;
}

/** Live height of the app scroller's visible area. */
export function useScrollportHeight(): number {
  const read = () => {
    const el = appScroller();
    if (el && el.clientHeight > 0) return el.clientHeight;
    return typeof window !== 'undefined' ? window.innerHeight - APP_HEADER_HEIGHT : 0;
  };
  const [height, setHeight] = useState<number>(read);

  useEffect(() => {
    const update = () => setHeight(read());
    update();
    window.addEventListener('resize', update);
    const el = appScroller();
    let ro: ResizeObserver | null = null;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    return () => {
      window.removeEventListener('resize', update);
      ro?.disconnect();
    };
  }, []);

  return height;
}
